<div class="space-y-6">
  <!-- Loading -->
  @if (isLoading() && !profile()) {
    <div class="flex items-center justify-center py-16">
      <div
        class="w-8 h-8 border-4 border-gray-200 border-t-gray-900 rounded-full animate-spin"
      ></div>
    </div>
  }

  @if (profile(); as p) {
    <!-- Profile info card -->
    <div
      class="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm"
    >
      <div class="p-6 pb-3">
        <h3 class="font-semibold text-lg">{{ 'SETTINGS.PROFILE.TITLE' | translate }}</h3>
        <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
          {{ 'SETTINGS.PROFILE.SUBTITLE' | translate }}
        </p>
      </div>

      <div class="p-6 pt-0 space-y-6">
        <!-- Avatar + name -->
        <div class="flex items-center gap-5">
          <!-- Avatar with upload overlay -->
          <div class="relative flex-shrink-0 group">
            @if (p.avatar) {
              <img
                [src]="p.avatar"
                [alt]="p.fullName"
                class="w-20 h-20 rounded-full object-cover border-2 border-gray-200 dark:border-gray-700"
              />
            } @else {
              <div
                class="w-20 h-20 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center text-white text-2xl font-bold select-none"
              >
                {{ initials || '?' }}
              </div>
            }

            <!-- Spinner overlay while uploading -->
            @if (avatarUploading()) {
              <div
                class="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center"
              >
                <div
                  class="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"
                ></div>
              </div>
            } @else {
              <!-- Hover overlay -->
              <div
                class="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1"
              >
                <label for="avatar-input" class="cursor-pointer flex flex-col items-center gap-0.5">
                  <i class="pi pi-camera text-white text-sm"></i>
                  <span class="text-white text-[10px] font-medium leading-none">{{
                    'SETTINGS.PROFILE.CHANGE_PHOTO' | translate
                  }}</span>
                </label>
                @if (p.avatar) {
                  <button
                    type="button"
                    (click)="removeAvatar()"
                    class="flex items-center gap-0.5 mt-0.5"
                    [title]="'SETTINGS.PROFILE.REMOVE_PHOTO_TITLE' | translate"
                  >
                    <i class="pi pi-trash text-red-300 text-xs"></i>
                    <span class="text-red-300 text-[10px] font-medium leading-none">{{
                      'SETTINGS.PROFILE.REMOVE_PHOTO' | translate
                    }}</span>
                  </button>
                }
              </div>
            }

            <input
              id="avatar-input"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              class="hidden"
              (change)="onAvatarSelected($event)"
              [disabled]="avatarUploading()"
            />
          </div>

          <div>
            <p class="text-xl font-semibold text-gray-900 dark:text-gray-100">{{ p.fullName }}</p>
            <span
              class="inline-block mt-1 px-3 py-0.5 text-xs rounded-full font-semibold"
              [class.bg-green-100]="!isEmployee"
              [class.text-green-700]="!isEmployee"
              [class.bg-emerald-100]="isEmployee"
              [class.text-emerald-700]="isEmployee"
            >
              {{ roleLabel }}
            </span>
            <p class="text-xs text-gray-400 dark:text-gray-500 mt-1.5">
              {{ 'SETTINGS.PROFILE.PHOTO_HINT' | translate }}
            </p>
          </div>
        </div>

        <div class="border-b border-gray-100 dark:border-gray-800"></div>

        <!-- Info grid -->
        <div class="grid gap-5 md:grid-cols-2">
          <!-- Full name -->
          <div class="space-y-1.5">
            <label
              for=""
              class="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1.5"
            >
              <lucide-icon [img]="User" class="w-4 h-4 text-gray-400"></lucide-icon>
              {{ 'SETTINGS.PROFILE.FULL_NAME' | translate }}
            </label>
            <div
              class="flex h-10 w-full items-center rounded-md border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 px-3 text-sm text-gray-900 dark:text-gray-100"
            >
              {{ p.fullName }}
            </div>
          </div>

          <!-- Phone -->
          <div class="space-y-1.5">
            <label
              for=""
              class="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1.5"
            >
              <lucide-icon [img]="Phone" class="w-4 h-4 text-gray-400"></lucide-icon>
              {{ 'SETTINGS.PROFILE.PHONE' | translate }}
            </label>
            <div
              class="flex h-10 w-full items-center rounded-md border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 px-3 text-sm text-gray-900 dark:text-gray-100"
            >
              {{ p.phoneNumber }}
            </div>
          </div>

          <!-- Role -->
          <div class="space-y-1.5">
            <label
              for=""
              class="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1.5"
            >
              <lucide-icon [img]="Shield" class="w-4 h-4 text-gray-400"></lucide-icon>
              {{ 'SETTINGS.PROFILE.ROLE' | translate }}
            </label>
            <div
              class="flex h-10 w-full items-center rounded-md border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 px-3 text-sm text-gray-900 dark:text-gray-100"
            >
              {{ roleLabel }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- POS PIN code (Employee va Owner/Customer uchun) -->
    @if (isPinUser) {
      <div
        class="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm"
      >
        <div class="p-6 pb-3">
          <h3 class="font-semibold text-lg flex items-center gap-2">
            <lucide-icon
              [img]="KeyRound"
              class="w-5 h-5 text-gray-600 dark:text-gray-400"
            ></lucide-icon>
            {{ 'SETTINGS.PROFILE.PIN_TITLE' | translate }}
          </h3>
          <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {{ 'SETTINGS.PROFILE.PIN_SUBTITLE' | translate }}
          </p>
        </div>

        <div class="p-6 pt-0 space-y-3">
          @if (pinError()) {
            <div
              class="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 text-red-600 rounded-lg text-sm"
            >
              <lucide-icon [img]="AlertCircle" class="w-4 h-4 flex-shrink-0"></lucide-icon>
              {{ pinError() }}
            </div>
          }

          <div
            class="rounded-lg border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 p-4"
          >
            <p
              class="text-xs text-gray-500 dark:text-gray-400 mb-2 font-medium uppercase tracking-wide"
            >
              {{ 'SETTINGS.PROFILE.PIN_LABEL' | translate }}
            </p>
            <div class="flex items-center gap-3">
              <span
                class="font-mono text-2xl font-bold tracking-[0.4em] flex-1 text-gray-900 dark:text-gray-100"
              >
                @if (pinVisible()) {
                  {{ p.pinCode || '—' }}
                } @else {
                  &#x2022;&#x2022;&#x2022;&#x2022;
                }
              </span>

              <!-- Toggle visibility -->
              <button
                (click)="togglePinVisibility()"
                class="inline-flex items-center justify-center rounded-md h-9 w-9 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors hover:cursor-pointer"
                [title]="
                  pinVisible()
                    ? ('SETTINGS.PROFILE.HIDE' | translate)
                    : ('SETTINGS.PROFILE.SHOW' | translate)
                "
              >
                @if (pinVisible()) {
                  <lucide-icon [img]="EyeOff" class="w-5 h-5 text-gray-500"></lucide-icon>
                } @else {
                  <lucide-icon [img]="Eye" class="w-5 h-5 text-gray-500"></lucide-icon>
                }
              </button>

              <!-- Regenerate -->
              <button
                (click)="regeneratePin()"
                [disabled]="isPinRegenerating()"
                class="inline-flex items-center justify-center gap-2 rounded-md h-9 px-4 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 text-sm font-medium transition-colors hover:cursor-pointer disabled:opacity-50"
                [title]="'SETTINGS.PROFILE.REGENERATE' | translate"
              >
                <lucide-icon
                  [img]="RefreshCw"
                  class="w-4 h-4"
                  [class.animate-spin]="isPinRegenerating()"
                ></lucide-icon>
                {{
                  isPinRegenerating()
                    ? ('SETTINGS.PROFILE.REGENERATING' | translate)
                    : ('SETTINGS.PROFILE.REGENERATE' | translate)
                }}
              </button>
            </div>
          </div>

          <p class="text-xs text-gray-400 dark:text-gray-500">
            {{ 'SETTINGS.PROFILE.PIN_HINT' | translate }}
          </p>
        </div>
      </div>
    }
  }
</div>
